/**
 * Loads the latest beerProfileVersions document for a beer (ITT-970 Engage inspect).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { BEER_PROFILE_VERSIONS_COLLECTION } from '../utils/firestoreCollections';

/**
 * @param {string | null | undefined} beerId
 * @returns {{
 *   version: Record<string, unknown> | null,
 *   loading: boolean,
 *   error: string | null,
 *   refresh: () => Promise<void>,
 * }}
 */
export default function useLatestBeerProfileVersion(beerId) {
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(Boolean(beerId));
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    const trimmedBeerId = typeof beerId === 'string' ? beerId.trim() : '';
    if (!trimmedBeerId) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      setVersion(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const versionsQuery = query(
        collection(db, BEER_PROFILE_VERSIONS_COLLECTION),
        where('beerId', '==', trimmedBeerId),
        orderBy('builtAt', 'desc'),
        limit(1),
      );
      const snapshot = await getDocs(versionsQuery);
      if (requestId !== requestIdRef.current) {
        return;
      }
      if (snapshot.empty) {
        setVersion(null);
        return;
      }
      const docSnap = snapshot.docs[0];
      setVersion({
        id: docSnap.id,
        ...docSnap.data(),
      });
    } catch (loadError) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      console.error('Failed to load beerProfileVersions', loadError);
      setVersion(null);
      setError(
        loadError instanceof Error
          ? loadError.message
          : 'Failed to load shadow profile comparison',
      );
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [beerId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    version,
    loading,
    error,
    refresh,
  };
}
